import { memo, useMemo, type MouseEvent, type ReactNode } from 'react';
import { PencilIcon, TrashIcon, type Icon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
    AddToReportSubmenuContent,
    Badge,
    Card,
    type CardState,
    ConfidenceBadge,
    EntityActionsMenu,
    ThumbnailCarousel,
} from '@/components';
import type { ActionMenuItem, ThumbnailQuickViewProps } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';

export type EventDisplayMode = 'alerts' | 'events';

const NO_EXTRA_ACTIONS: ActionMenuItem[] = [];

export interface EventCardMetadataItem {
    icon: Icon;
    text: string;
    title?: string;
}

export interface EventCardProps {
    id: string;
    title: string;
    thumbnailUrls?: string[];
    licensePlate?: string;
    confidence?: number;
    metadata: EventCardMetadataItem[];
    quickView?: ThumbnailQuickViewProps;
    isScene?: boolean;
    onClick?: (id: string, event: MouseEvent<HTMLDivElement>) => void;
    onEdit?: (id: string) => void;
    onDelete?: (id: string) => void;
    extraActions?: ActionMenuItem[];
    editLabel?: string;
    deleteLabel?: string;
    selectable?: boolean;
    selected?: boolean;
    onSelectToggle?: (id: string) => void;
    dragIndicator?: ReactNode;
    state?: CardState;
    className?: string;
}

function EventCardBase({
    id,
    title,
    thumbnailUrls,
    licensePlate,
    confidence,
    metadata,
    quickView,
    isScene,
    onClick,
    onEdit,
    onDelete,
    extraActions = NO_EXTRA_ACTIONS,
    editLabel,
    deleteLabel,
    selectable = false,
    selected = false,
    onSelectToggle,
    dragIndicator,
    state,
    className,
}: EventCardProps) {
    const { t } = useTranslation(I18nNamespace.EVENTS);

    const menuItems = useMemo<ActionMenuItem[]>(() => {
        const items: ActionMenuItem[] = [...extraActions];
        if (onEdit) {
            items.push({
                key: 'edit',
                label: editLabel ?? t('actions.edit'),
                icon: PencilIcon,
                onSelect: () => onEdit(id),
            });
        }
        if (onDelete) {
            items.push({
                key: 'delete',
                label: deleteLabel ?? t('actions.delete'),
                icon: TrashIcon,
                variant: 'danger',
                onSelect: () => onDelete(id),
            });
        }
        return items;
    }, [id, t, extraActions, onEdit, onDelete, editLabel, deleteLabel]);

    return (
        <Card
            className={className}
            state={state}
            dataEventId={id}
            thumbnailMedia={
                <ThumbnailCarousel
                    imageUrls={thumbnailUrls ?? []}
                    imageUsesAuth
                />
            }
            thumbnailLicensePlate={licensePlate}
            bottomLeftSlot={dragIndicator}
            topLeftSlot={
                isScene || confidence !== undefined ? (
                    <div className="flex items-center gap-1">
                        {confidence !== undefined && (
                            <ConfidenceBadge
                                value={confidence}
                                glass
                                compact
                                className="pointer-events-none h-[22px] min-h-[22px] gap-1 px-1.5 py-0.5 backdrop-blur-[20px]"
                            />
                        )}
                        {isScene && (
                            <Badge variant="neutral-glass">
                                {t('scene.badge', {
                                    defaultValue: 'Scene',
                                })}
                            </Badge>
                        )}
                    </div>
                ) : undefined
            }
            thumbnailQuickView={quickView}
            title={title}
            metadata={metadata}
            selectable={selectable}
            checked={selected}
            onCheckedChange={() => onSelectToggle?.(id)}
            sideAction={
                menuItems.length > 0 && (
                    <EntityActionsMenu
                        wrapperClassName="flex-shrink-0"
                        triggerAriaLabel={t('actions.eventActions')}
                        items={menuItems}
                    />
                )
            }
            onClick={(eventClick) =>
                selectable ? onSelectToggle?.(id) : onClick?.(id, eventClick)
            }
        />
    );
}

EventCardBase.displayName = 'EventCard';
export const EventCard = memo(EventCardBase);
